// ============================================
// アイテムエンティティの生成と適用
// ============================================

import { CONFIG } from '../../config/game-config';
import {
    getSelectedShip,
    getItems,
    getPowerLevel,
    setPowerLevel,
    getPowerStock,
    setPowerStock,
    getScore,
    setScore,
    setShieldActive,
    setShieldTimer,
    setShieldCharging,
    getBombStock,
    setBombStock,
    getHomingStock,
    setHomingStock,
    getCannonStock,
    setCannonStock
} from '../../store/index';
import { getDiffConfig } from '../../utils/helpers';
import { getP5 } from '../../core/p5-instance';
import type { Item, ShipType } from '../../types/game-types';

// ============================================
// spawnItem() - アイテムの生成
// ============================================
function spawnItem(x: number, y: number): void {
    const p = getP5();
    const selectedShip: ShipType = getSelectedShip();
    const items = getItems();

    const itemRoll = p.random(100);
    let item: Item;

    // 特殊系アイテムの出現判定（機体に合わせる）
    const specialThreshold = 8;  // 特殊系の出現確率8%
    const powerThreshold = specialThreshold + 30;  // パワー30%
    const scoreThreshold = powerThreshold + 30; // スコア30%
    // 残りがシールド

    if (itemRoll < specialThreshold) {
        // 特殊系アイテム（機体に応じて変化）
        if (selectedShip === 'bomber') {
            // ボムアイテム
            item = {
                x: x,
                y: y,
                size: 28,
                type: 'bomb',
                color: p.color(0, 240, 255),
                rotation: 0
            };
        } else if (selectedShip === 'hunter') {
            // ホーミングアイテム
            item = {
                x: x,
                y: y,
                size: 28,
                type: 'homing',
                color: p.color(255, 150, 0),
                rotation: 0
            };
        } else if (selectedShip === 'lancer') {
            // 荷電粒子砲アイテム
            item = {
                x: x,
                y: y,
                size: 28,
                type: 'cannon',
                color: p.color(200, 50, 255),
                rotation: 0
            };
        } else {
            // Fallback to shield if ship is unknown
            item = {
                x: x,
                y: y,
                size: 25,
                type: 'shield',
                color: p.color(100, 200, 255),
                rotation: 0
            };
        }
    } else if (itemRoll < powerThreshold) {
        // パワーアップ - 緑の雷マーク
        item = {
            x: x,
            y: y,
            size: 25,
            type: 'power',
            color: p.color(0, 255, 100),
            rotation: 0
        };
    } else if (itemRoll < scoreThreshold) {
        // スコアボーナス - 金の星
        item = {
            x: x,
            y: y,
            size: 25,
            type: 'power', // Note: original code has 'score' but Item type doesn't have it
            color: p.color(255, 215, 0),
            rotation: 0
        };
    } else {
        // シールド - 青の六角形
        item = {
            x: x,
            y: y,
            size: 25,
            type: 'shield',
            color: p.color(100, 200, 255),
            rotation: 0
        };
    }

    items.push(item);
}

// ============================================
// applyItem() - アイテム効果の適用
// ============================================
type ItemType = 'power' | 'heal' | 'bomb' | 'homing' | 'cannon' | 'shield' | 'score';

function applyItem(type: ItemType): void {
    const p = getP5();
    const selectedShip: ShipType = getSelectedShip();
    const shipCfg = CONFIG.ships[selectedShip];
    const diffCfg = getDiffConfig();

    if (type === 'power') {
        // パワーアップ: ストックを増やし、必要数に達したらレベルアップ
        let powerLevel = getPowerLevel();
        if (powerLevel < shipCfg.powerMaxLevel) {
            let powerStock = getPowerStock();
            powerStock++;
            setPowerStock(powerStock);

            const required = shipCfg.powerRequired[powerLevel - 1] || 1;
            if (powerStock >= required) {
                powerLevel++;
                setPowerLevel(powerLevel);
                setPowerStock(0);
            }
        }
    } else if (type === 'score') {
        let score = getScore();
        score += CONFIG.item.scoreBonus;
        setScore(score);
    } else if (type === 'shield') {
        setShieldActive(true);
        setShieldTimer(shipCfg.shieldDuration);
        setShieldCharging(true);
    } else if (type === 'bomb') {
        const bombStock = getBombStock();
        setBombStock(p.min(bombStock + 1, diffCfg.maxBombs));
    } else if (type === 'homing') {
        const homingStock = getHomingStock();
        setHomingStock(p.min(homingStock + 1, diffCfg.maxHoming));
    } else if (type === 'cannon') {
        const cannonStock = getCannonStock();
        setCannonStock(p.min(cannonStock + 1, diffCfg.maxCannon));
    }
}

export { spawnItem, applyItem };
